import { useState } from "react";
import { MapPin, Expand } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang } from "@/i18n/LanguageContext";
import { IMAGES, PROJECTS, PROJECT_FILTERS, pick } from "@/data/site";

const STATUS_PILL = {
  Completed: "bg-emerald-500 text-white",
  "In Progress": "bg-amber-500 text-white",
  Upcoming: "bg-blue-500 text-white",
};

const CAT_TEXT = ["text-orange-600", "text-teal-600", "text-amber-600", "text-purple-600"];

export default function Projects() {
  const { t, lang } = useLang();
  const [filter, setFilter] = useState("All");
  const list = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div data-testid="projects-page">
      <Seo
        title="Projects — V.B. Sharada Associates | Villas, Commercial Buildings & Interiors in Telangana"
        description="Portfolio of residential villas, commercial complexes, interior projects and structural planning work delivered by V.B. Sharada Associates across Tandur and Telangana."
      />

      <section className="warm-grid border-b border-orange-100">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-14 md:py-20">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            {t("prj.eyebrow")}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 max-w-3xl">
            {t("prj.h1")}
          </h1>
          <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-2xl">{t("prj.para")}</p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="project-filters">
            {PROJECT_FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`px-5 py-2 text-sm font-semibold rounded-full border-2 transition-colors duration-200 ${
                  filter === f
                    ? "bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-500/25"
                    : "bg-white text-slate-600 border-slate-200 hover:border-orange-400 hover:text-orange-600"
                }`}
              >
                {t(`cat.${f}`)}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {list.map((p, i) => (
              <article
                key={p.id}
                data-testid={`project-card-${p.id}`}
                className="group bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={IMAGES[p.image]}
                    alt={`${p.title} — ${p.category} project in ${p.location}, Telangana`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className={`absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow ${STATUS_PILL[p.status]}`}>
                    {t(`status.${p.status}`)}
                  </span>
                </div>
                <div className="p-5">
                  <div className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${CAT_TEXT[i % CAT_TEXT.length]}`}>
                    {t(`cat.${p.category}`)}
                  </div>
                  <h2 className="font-heading text-lg font-semibold text-slate-900">{p.title}</h2>
                  <div className="mt-3 flex flex-col gap-1.5 text-xs text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-orange-500" /> {t(`loc.${p.location}`)}, {t("loc.Telangana")}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Expand className="w-3.5 h-3.5 text-teal-600" /> {pick(p, "scale", lang)}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
          {list.length === 0 && (
            <p className="text-slate-500 text-sm" data-testid="projects-empty">
              {t("prj.empty")}
            </p>
          )}
          <p className="mt-10 text-xs text-slate-400 font-mono uppercase tracking-widest">{t("prj.disclaimer")}</p>
        </div>
      </section>
    </div>
  );
}
